<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import type { StageSegment, StageStrip } from '../../utils/requestStatusViewUtils'

// The header card's pipeline strip: "Stage 2 of 4 · Owner Onboarding", what
// comes next, then one column per stage — a bar, a numbered dot (a tick once
// done) with the stage name, and a caption saying how that stage went. The
// trailing "Properties" column is where a finished request hands off to.
// Every word comes from `buildStageStrip`; this only lays it out.
defineProps<{ strip: StageStrip }>()

function captionColor(segment: StageSegment): string {
  if (segment.state === 'done') return 'success'
  if (segment.state === 'upcoming') return 'var(--p-sliceal-text-subtle)'
  if (segment.tone === 'attention') return 'warning'
  if (segment.tone === 'negative') return 'danger'
  return 'primary'
}
</script>

<template>
  <Box direction="col" gap="0.75rem" class="progress">
    <Box flex align="center" justify="between" gap="3" class="progress__head">
      <Text is="span" type="body-sm" weight="semibold" color="default">{{ strip.title }}</Text>
      <Text v-if="strip.next" is="span" type="caption" color="var(--p-sliceal-text-subtle)">
        Next: {{ strip.next }}
      </Text>
    </Box>

    <Box is="ol" flex align="start" gap="0.75rem" class="progress__track">
      <Box
        v-for="segment in strip.segments"
        :key="segment.key"
        is="li"
        direction="col"
        gap="2"
        class="progress__segment"
        :class="[
          `progress__segment--${segment.state}`,
          `progress__segment--tone-${segment.tone}`,
          { 'progress__segment--handoff': segment.number === null },
        ]"
        :aria-current="segment.state === 'active' ? 'step' : undefined"
      >
        <Box class="progress__bar" aria-hidden="true" />
        <Box flex align="center" gap="0.375rem" class="progress__title">
          <PiIcon v-if="segment.number === null" name="arrow-right" class="progress__handoff" />
          <Box v-else flex align="center" justify="center" class="progress__dot" aria-hidden="true">
            <PiIcon v-if="segment.state === 'done'" name="check" class="progress__dot-check" />
            <Text
              v-else
              is="span"
              type="micro"
              :color="segment.state === 'active' ? 'inverse' : 'var(--p-sliceal-text-subtle)'"
              class="progress__dot-number"
            >
              {{ segment.number }}
            </Text>
          </Box>
          <Text
            is="span"
            type="body-sm"
            weight="semibold"
            :color="segment.state === 'upcoming' ? 'var(--p-sliceal-text-subtle)' : 'default'"
            class="progress__label"
          >
            {{ segment.label }}
          </Text>
        </Box>
        <Text
          is="span"
          type="caption"
          :weight="segment.state === 'upcoming' ? 'medium' : 'semibold'"
          :color="captionColor(segment)"
          class="progress__caption"
        >
          {{ segment.caption }}
        </Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.progress__head {
  flex-wrap: wrap;
}

.progress__track {
  margin: 0;
  padding: 0;
  list-style: none;
}

.progress__segment {
  flex: 1 1 0;
  min-width: 0;
}

/* The hand-off is not a stage of the request, so it takes less room. */
.progress__segment--handoff {
  flex-grow: 0.7;
}

.progress__bar {
  height: 4px;
  border-radius: 2px;
  background: var(--p-surface-200);
}

.progress__segment--done .progress__bar {
  background: var(--p-success-600);
}

.progress__segment--active.progress__segment--tone-normal .progress__bar {
  background: var(--p-primary-600);
}

.progress__segment--active.progress__segment--tone-attention .progress__bar {
  background: var(--p-warning-500);
}

.progress__segment--active.progress__segment--tone-negative .progress__bar {
  background: var(--p-danger-500);
}

.progress__title {
  min-width: 0;
}

.progress__dot {
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 1px solid var(--p-surface-300);
  background: var(--p-surface-0);
}

.progress__segment--done .progress__dot {
  border-color: var(--p-success-600);
  background: var(--p-success-600);
  color: var(--p-surface-0);
}

.progress__segment--active .progress__dot {
  border-color: var(--p-primary-600);
  background: var(--p-primary-600);
}

.progress__segment--active.progress__segment--tone-attention .progress__dot {
  border-color: var(--p-warning-500);
  background: var(--p-warning-500);
}

.progress__segment--active.progress__segment--tone-negative .progress__dot {
  border-color: var(--p-danger-500);
  background: var(--p-danger-500);
}

.progress__dot-check {
  font-size: 0.5rem;
}

.progress__dot-number {
  line-height: 1;
}

.progress__handoff {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--p-sliceal-text-subtle);
}

.progress__segment--done .progress__handoff {
  color: var(--p-success-600);
}

.progress__label,
.progress__caption {
  word-break: break-word;
}

/* Below tablet the five columns would wrap to three lines each; the bars stay
   and only the active stage keeps its words, which the title already names. */
@media (max-width: 992px) {
  .progress__segment:not(.progress__segment--active) .progress__title,
  .progress__segment:not(.progress__segment--active) .progress__caption {
    display: none;
  }

  .progress__segment--active {
    flex-grow: 3;
  }
}
</style>
